// Voice profile. Renders, top to bottom:
//
//   1. Page title + meta line
//   2. Voice signature line   — "Direct and clear. Adds context when..."
//   3. Radar (left) + By context (right)
//   4. Stat grid              — pace / filler / hedges / talk-share
//   5. What to work on        — actionable behaviour-pattern advice
//
// Hedges-to-watch is rolled into the broader "what to work on" list so the
// page reads as a tight, actionable summary rather than a stat dump.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import {
  callTypeToCategory,
  getPatterns,
  getTrends,
  listAnalyses,
  SessionExpiredError,
  type AnalysisListItem,
  type CallCategory,
  type Dimension,
  type PatternsResponse,
  type TrendsResponse,
} from "../lib/api";
import { Crumbs } from "../components/Crumbs";
import { RadarChart } from "../components/RadarChart";
import { StatGrid, type Stat } from "../components/StatGrid";
import { ByContext, type ContextRow } from "../components/ByContext";
import { ThingsToWorkOn, buildInsights } from "../components/ThingsToWorkOn";
import { Typewriter } from "../components/Typewriter";

const VP_LIMIT = 60;
const MIN_READINGS = 5;

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; trends: TrendsResponse; patterns: PatternsResponse; analyses: AnalysisListItem[] }
  | { kind: "error"; message: string };

export function VoiceProfile() {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  const load = useCallback(async () => {
    try {
      const [trends, patterns, analyses] = await Promise.all([
        getTrends({ window: "30d" }),
        getPatterns({ days: 60 }),
        listAnalyses({ limit: VP_LIMIT }),
      ]);
      setState({ kind: "ok", trends, patterns, analyses: analyses.items });
    } catch (err) {
      if (err instanceof SessionExpiredError) {
        navigate("/login", { replace: true });
        return;
      }
      setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }, [navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  const radarScores = useMemo(() => {
    if (state.kind !== "ok") return null;
    const out: Record<Dimension, number | null> = {
      clarity: null,
      confidence: null,
      conciseness: null,
      listening: null,
      structure: null,
      pacing: null,
    };
    for (const d of state.trends.dimensions) out[d.name] = d.average;
    return out;
  }, [state]);

  const stats = useMemo(() => (state.kind === "ok" ? computeStats(state.patterns) : []), [state]);
  const insights = useMemo(() => (state.kind === "ok" ? buildInsights(state.patterns.perBehaviour) : []), [state]);
  const contextRows = useMemo(() => (state.kind === "ok" ? computeByContext(state.analyses) : []), [state]);
  const contextTail = useMemo(() => (contextRows.length >= 2 ? makeContextTail(contextRows) : undefined), [contextRows]);

  const signature = useMemo(() => {
    if (state.kind !== "ok") return null;
    const dims = state.trends.dimensions;
    if (dims.length === 0 || dims.every((d) => d.average == null)) return null;
    const top = dims.slice().sort((a, b) => (b.average ?? 0) - (a.average ?? 0))[0]!;
    const bottom = dims.slice().sort((a, b) => (a.average ?? 0) - (b.average ?? 0))[0]!;
    return { lead: leadPhrase(top.name), trail: trailPhrase(bottom.name) };
  }, [state]);

  const meetingMix = useMemo(() => (state.kind === "ok" ? makeMeetingMix(state.analyses) : null), [state]);

  return (
    <>
      <Crumbs trail={["Voice profile"]} />
      <div className="page-h">
        <div>
          <h1>Voice profile.</h1>
          <p className="sub">
            {state.kind === "ok" && meetingMix
              ? `Built from ${state.patterns.meetingCount} readings. ${meetingMix}.`
              : "Your communication signature, distilled from every reading."}
          </p>
        </div>
      </div>

      {state.kind === "loading" ? <LoadingSkeleton /> : null}
      {state.kind === "error" ? (
        <p style={{ color: "rgb(var(--color-warning))" }}>{state.message}</p>
      ) : null}

      {state.kind === "ok" && state.patterns.meetingCount < MIN_READINGS ? (
        <SparseState count={state.patterns.meetingCount} />
      ) : null}

      {state.kind === "ok" && state.patterns.meetingCount >= MIN_READINGS && radarScores && signature ? (
        <>
          <section className="voice-summary">
            <p className="l">Your voice</p>
            <h2>
              {signature.lead}{" "}
              <span className="em">
                {/* The lead phrase is who you are; the trail is what the
                    system has noticed. Type the trail out so the user feels
                    the AI thinking through its observation. Once per session;
                    instant on revisit. */}
                <Typewriter
                  text={signature.trail}
                  speed={28}
                  startDelay={420}
                  sessionKey="klemento.tw.signature"
                />
              </span>
            </h2>
          </section>

          <section className="vp-h">
            <div className="kpanel" style={{ padding: 0 }}>
              <div className="radar-wrap">
                <RadarChart scores={radarScores} />
              </div>
            </div>
            <ByContext rows={contextRows} tail={contextTail} />
          </section>

          <StatGrid stats={stats} />

          <ThingsToWorkOn insights={insights} />
        </>
      ) : null}
    </>
  );
}

function LoadingSkeleton() {
  return (
    <div aria-hidden="true" style={{ opacity: 0.5 }}>
      <section className="voice-summary">
        <p className="l">Your voice</p>
        <h2>Computing.</h2>
      </section>
      <div className="vp-h">
        <div className="kpanel" style={{ padding: 0, minHeight: 240 }} />
        <div className="kpanel" style={{ minHeight: 200 }} />
      </div>
    </div>
  );
}

function SparseState({ count }: { count: number }) {
  return (
    <div className="kit-empty" style={{ padding: "48px 32px" }}>
      <p style={{ margin: "0 0 12px", fontSize: 16, color: "rgb(var(--color-ink-soft))", fontFamily: "var(--font-display)", fontWeight: 600 }}>
        Bring 5+ readings to see your voice profile.
      </p>
      <p style={{ margin: "0 0 18px", fontSize: 13, color: "rgb(var(--color-ink-muted))" }}>
        You're at {count}. The radar gets sharper as patterns settle.
      </p>
      <Link to="/settings" className="btn btn-primary">Connect a notetaker</Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Voice signature templates.

function leadPhrase(name: Dimension): string {
  switch (name) {
    case "clarity": return "Direct and clear.";
    case "confidence": return "Confident and grounded.";
    case "conciseness": return "Tight and disciplined.";
    case "listening": return "Curious and patient.";
    case "structure": return "Structured and methodical.";
    case "pacing": return "Measured and steady.";
  }
}

function trailPhrase(name: Dimension): string {
  switch (name) {
    case "clarity": return "Loosens when ideas are still forming.";
    case "confidence": return "Hedges when stakes feel high.";
    case "conciseness": return "Adds context when it isn't asked for.";
    case "listening": return "Cuts in when ideas come faster than turns.";
    case "structure": return "Drifts when thinking out loud.";
    case "pacing": return "Speeds up when reading the room.";
  }
}

// ---------------------------------------------------------------------------
// Stats. 4 stats from getPatterns.perBehaviour.

function computeStats(patterns: PatternsResponse): Stat[] {
  const byName = new Map<string, number>();
  for (const b of patterns.perBehaviour) byName.set(b.name, b.averageCount);

  return [
    {
      label: "Avg pace",
      value: byName.has("speaking_pace") ? `${Math.round(byName.get("speaking_pace")!)}` : "—",
      unit: "wpm",
      sub: "healthy 150–180",
    },
    {
      label: "Filler / reading",
      value: byName.has("filler_words") ? `${byName.get("filler_words")!.toFixed(1)}` : "—",
      sub: "lower is better",
    },
    {
      label: "Hedges / reading",
      value: byName.has("position_weighted_hedges") ? `${byName.get("position_weighted_hedges")!.toFixed(1)}` : "—",
      sub: "watch when high",
    },
    {
      label: "Talk share",
      value: byName.has("talk_time_ratio") ? `${Math.round(byName.get("talk_time_ratio")!)}` : "—",
      unit: "%",
      sub: "across all readings",
    },
  ];
}

// ---------------------------------------------------------------------------
// By-context bars + meta strings.

const CATEGORY_LABEL: Record<Exclude<CallCategory, "general">, string> = {
  one_on_one: "1:1",
  sales: "Sales",
  hiring: "Hiring",
  team: "Team",
  leadership: "Leadership",
  external: "External",
};

function computeByContext(items: AnalysisListItem[]): ContextRow[] {
  const buckets = new Map<string, { sum: number; n: number }>();
  for (const it of items) {
    if (typeof it.overallScore !== "number") continue;
    const cat = callTypeToCategory(it.callType);
    const cur = buckets.get(cat) ?? { sum: 0, n: 0 };
    cur.sum += it.overallScore;
    cur.n += 1;
    buckets.set(cat, cur);
  }
  const rows: ContextRow[] = [];
  for (const [cat, { sum, n }] of buckets.entries()) {
    rows.push({
      name: CATEGORY_LABEL[cat as Exclude<CallCategory, "general">] ?? cat,
      score: sum / n,
      readings: n,
    });
  }
  rows.sort((a, b) => b.score - a.score);
  return rows;
}

function makeContextTail(rows: ContextRow[]): string {
  const top = rows[0]!;
  const bottom = rows[rows.length - 1]!;
  if (top.name === bottom.name) return "";
  return `${top.name} is your strongest context, ${bottom.name} is the one to keep working on.`;
}

function makeMeetingMix(items: AnalysisListItem[]): string {
  const buckets = new Map<string, number>();
  for (const it of items) {
    const cat = callTypeToCategory(it.callType);
    buckets.set(cat, (buckets.get(cat) ?? 0) + 1);
  }
  const parts: string[] = [];
  for (const [cat, n] of buckets.entries()) {
    const label = CATEGORY_LABEL[cat as Exclude<CallCategory, "general">] ?? cat;
    parts.push(`${n} ${label.toLowerCase()}`);
  }
  return parts.join(", ");
}
